import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Refresh,
  CheckCircle,
  AttachMoney,
  TrendingDown,
  Recycling,
  ThumbUp,
  Cancel,
  PlayArrow,
  Done,
  ThumbDown,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  fetchScrapRecords,
  createScrapRecord,
  approveScrapRecord,
  rejectScrapRecord,
  createReworkOrder,
  fetchReworkOrders,
  updateReworkStatus,
  fetchScrapAnalytics,
  clearError,
  ScrapReason,
  ScrapDisposition,
  ScrapStatus,
  ReworkStatus,
  ScrapRecord,
  ReworkOrder,
} from '../../store/slices/scrapSlice';

// ─── Constants ────────────────────────────────────────────────────────────────

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

const REASON_COLORS: Record<string, string> = {
  defective_material:    '#f44336',
  process_error:         '#ff9800',
  equipment_malfunction: '#9c27b0',
  operator_error:        '#2196f3',
  design_issue:          '#00bcd4',
  damage:                '#4caf50',
  obsolescence:          '#795548',
  excess_inventory:      '#607d8b',
};

const PIE_COLORS = ['#f44336', '#ff9800', '#9c27b0', '#2196f3', '#00bcd4', '#4caf50', '#795548', '#607d8b'];

const SCRAP_REASON_LABELS: Record<ScrapReason, string> = {
  [ScrapReason.DEFECTIVE_MATERIAL]:    'Defective Material',
  [ScrapReason.PROCESS_ERROR]:         'Process Error',
  [ScrapReason.EQUIPMENT_MALFUNCTION]: 'Equipment Malfunction',
  [ScrapReason.OPERATOR_ERROR]:        'Operator Error',
  [ScrapReason.DESIGN_ISSUE]:          'Design Issue',
  [ScrapReason.DAMAGE]:                'Damage',
  [ScrapReason.OBSOLESCENCE]:          'Obsolescence',
  [ScrapReason.EXCESS_INVENTORY]:      'Excess Inventory',
};

const DISPOSITION_LABELS: Record<ScrapDisposition, string> = {
  [ScrapDisposition.SCRAP]:               'Scrap',
  [ScrapDisposition.REWORK]:              'Rework',
  [ScrapDisposition.USE_AS_IS]:           'Use As-Is',
  [ScrapDisposition.RETURN_TO_SUPPLIER]:  'Return to Supplier',
  [ScrapDisposition.RECLAIM_MATERIAL]:    'Reclaim Material',
};

// ─── Item type ────────────────────────────────────────────────────────────────

interface MasterDataItem {
  id: string;
  itemCode: string;
  name: string;
  [key: string]: any;
}

// ─── Empty form states ────────────────────────────────────────────────────────

const EMPTY_SCRAP_FORM = {
  itemId:       '',
  batchNumber:  '',
  quantity:     1,
  scrapReason:  ScrapReason.PROCESS_ERROR as ScrapReason,
  defectType:   'visual' as string,
  disposition:  ScrapDisposition.SCRAP as ScrapDisposition,
  materialCost: 0,
  laborCost:    0,
  overheadCost: 0,
  notes:        '',
};

const EMPTY_REWORK_FORM = {
  scrapRecordId:      '',
  reworkInstructions: '',
  estimatedCost:      0,
  estimatedTime:      0,
  assignedTo:         '',
};

// ─── Helper functions ─────────────────────────────────────────────────────────

function formatCurrency(val: number): string {
  return val.toLocaleString('en-KE', { style: 'currency', currency: 'KES' });
}

function getScrapStatusColor(status: ScrapStatus): 'warning' | 'success' | 'error' | 'default' {
  switch (status) {
    case ScrapStatus.PENDING:  return 'warning';
    case ScrapStatus.APPROVED: return 'success';
    case ScrapStatus.REJECTED: return 'error';
    default:                   return 'default';
  }
}

function getReworkStatusColor(status: ReworkStatus): 'default' | 'info' | 'success' | 'error' {
  switch (status) {
    case ReworkStatus.PENDING:     return 'default';
    case ReworkStatus.IN_PROGRESS: return 'info';
    case ReworkStatus.COMPLETED:   return 'success';
    case ReworkStatus.CANCELLED:   return 'error';
    default:                       return 'default';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const Scrap: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { scrapRecords, reworkOrders, analytics, isLoading, error, serviceUnavailable } =
    useSelector((state: RootState) => state.scrap);

  // Tab state
  const [selectedTab, setSelectedTab] = useState(0);

  // Filter state
  const [filterDateFrom,    setFilterDateFrom]    = useState('');
  const [filterDateTo,      setFilterDateTo]      = useState('');
  const [filterReason,      setFilterReason]      = useState('');
  const [filterStatus,      setFilterStatus]      = useState('');

  // Snackbar
  const [snackbarOpen,    setSnackbarOpen]    = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Create scrap dialog
  const [createScrapOpen,    setCreateScrapOpen]    = useState(false);
  const [scrapForm,          setScrapForm]          = useState({ ...EMPTY_SCRAP_FORM });
  const [createScrapLoading, setCreateScrapLoading] = useState(false);
  const [createScrapError,   setCreateScrapError]   = useState<string | null>(null);

  // Item autocomplete
  const [items,             setItems]             = useState<MasterDataItem[]>([]);
  const [itemsLoading,      setItemsLoading]      = useState(false);
  const [itemsFailed,       setItemsFailed]       = useState(false);
  const [selectedItem,      setSelectedItem]      = useState<MasterDataItem | null>(null);

  // Create rework dialog
  const [createReworkOpen,    setCreateReworkOpen]    = useState(false);
  const [reworkForm,          setReworkForm]          = useState({ ...EMPTY_REWORK_FORM });
  const [createReworkLoading, setCreateReworkLoading] = useState(false);
  const [createReworkError,   setCreateReworkError]   = useState<string | null>(null);

  // Rework complete dialog
  const [completeReworkOpen,   setCompleteReworkOpen]   = useState(false);
  const [completeReworkId,     setCompleteReworkId]     = useState('');
  const [completionNotes,      setCompletionNotes]      = useState('');
  const [completeReworkLoading, setCompleteReworkLoading] = useState(false);

  // Rework cancel dialog
  const [cancelReworkOpen,    setCancelReworkOpen]    = useState(false);
  const [cancelReworkId,      setCancelReworkId]      = useState('');
  const [cancelReworkLoading, setCancelReworkLoading] = useState(false);

  // Reject scrap dialog
  const [rejectScrapOpen,    setRejectScrapOpen]    = useState(false);
  const [rejectScrapId,      setRejectScrapId]      = useState('');
  const [rejectionReason,    setRejectionReason]    = useState('');
  const [rejectScrapLoading, setRejectScrapLoading] = useState(false);

  // Load data on mount
  useEffect(() => {
    dispatch(fetchScrapRecords({}));
    dispatch(fetchScrapAnalytics());
    dispatch(fetchReworkOrders());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchScrapRecords({
      dateFrom:    filterDateFrom || undefined,
      dateTo:      filterDateTo   || undefined,
      scrapReason: filterReason   || undefined,
      status:      filterStatus   || undefined,
    }));
    dispatch(fetchScrapAnalytics());
    dispatch(fetchReworkOrders());
  };

  const handleApplyFilters = () => {
    dispatch(fetchScrapRecords({
      dateFrom:    filterDateFrom || undefined,
      dateTo:      filterDateTo   || undefined,
      scrapReason: filterReason   || undefined,
      status:      filterStatus   || undefined,
    }));
  };

  const handleApprove = async (id: string) => {
    await dispatch(approveScrapRecord(id));
  };

  // ── Fetch items when scrap dialog opens ──────────────────────────────────────

  const handleOpenCreateScrap = async () => {
    setCreateScrapOpen(true);
    setItemsLoading(true);
    setItemsFailed(false);
    setSelectedItem(null);
    setScrapForm({ ...EMPTY_SCRAP_FORM });
    setCreateScrapError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/master-data/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setItemsFailed(true);
      } else {
        const data = await res.json();
        const raw: any[] = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setItems(raw.map(i => ({ id: String(i.id ?? i._id ?? ''), itemCode: String(i.itemCode ?? i.item_code ?? ''), name: String(i.name ?? '') })));
      }
    } catch {
      setItemsFailed(true);
    } finally {
      setItemsLoading(false);
    }
  };

  // ── Create scrap record ──────────────────────────────────────────────────────

  const totalCost = scrapForm.materialCost + scrapForm.laborCost + scrapForm.overheadCost;

  const handleCreateScrap = async () => {
    setCreateScrapLoading(true);
    setCreateScrapError(null);
    try {
      await dispatch(createScrapRecord({
        itemId:      scrapForm.itemId,
        batchNumber: scrapForm.batchNumber || undefined,
        quantity:    scrapForm.quantity,
        scrapReason: scrapForm.scrapReason,
        defectType:  scrapForm.defectType,
        disposition: scrapForm.disposition,
        costs: {
          materialCost:  scrapForm.materialCost,
          laborCost:     scrapForm.laborCost,
          overheadCost:  scrapForm.overheadCost,
          totalCost,
          netLoss:       totalCost,
        },
        notes:     scrapForm.notes || undefined,
        status:    ScrapStatus.PENDING,
        scrapDate: new Date().toISOString(),
        reportedBy: '',  // backend fills from auth context
      })).unwrap();
      setCreateScrapOpen(false);
      setScrapForm({ ...EMPTY_SCRAP_FORM });
      setSelectedItem(null);
    } catch (err: any) {
      setCreateScrapError(err?.message ?? 'Failed to create scrap record. Please try again.');
    } finally {
      setCreateScrapLoading(false);
    }
  };

  // ── Create rework order ──────────────────────────────────────────────────────

  const handleCreateRework = async () => {
    setCreateReworkLoading(true);
    setCreateReworkError(null);
    try {
      await dispatch(createReworkOrder({
        scrapRecordId:      reworkForm.scrapRecordId,
        reworkInstructions: reworkForm.reworkInstructions,
        estimatedCost:      reworkForm.estimatedCost,
        estimatedTime:      reworkForm.estimatedTime,
        assignedTo:         reworkForm.assignedTo,
        status:             ReworkStatus.PENDING,
      })).unwrap();
      setCreateReworkOpen(false);
      setReworkForm({ ...EMPTY_REWORK_FORM });
    } catch (err: any) {
      setCreateReworkError(err?.message ?? 'Failed to create rework order. Please try again.');
    } finally {
      setCreateReworkLoading(false);
    }
  };

  // ── Rework lifecycle handlers ────────────────────────────────────────────────

  const handleStartRework = async (id: string) => {
    await dispatch(updateReworkStatus({ id, status: 'in_progress' }));
    dispatch(fetchReworkOrders());
  };

  const handleOpenComplete = (id: string) => {
    setCompleteReworkId(id);
    setCompletionNotes('');
    setCompleteReworkOpen(true);
  };

  const handleConfirmComplete = async () => {
    setCompleteReworkLoading(true);
    try {
      await dispatch(updateReworkStatus({ id: completeReworkId, status: 'completed', completionNotes })).unwrap();
      setCompleteReworkOpen(false);
      setCompletionNotes('');
      setCompleteReworkId('');
      dispatch(fetchReworkOrders());
    } catch {
      // error handled by slice
    } finally {
      setCompleteReworkLoading(false);
    }
  };

  const handleOpenCancel = (id: string) => {
    setCancelReworkId(id);
    setCancelReworkOpen(true);
  };

  const handleConfirmCancel = async () => {
    setCancelReworkLoading(true);
    try {
      await dispatch(updateReworkStatus({ id: cancelReworkId, status: 'cancelled' })).unwrap();
      setCancelReworkOpen(false);
      setCancelReworkId('');
      dispatch(fetchReworkOrders());
    } catch {
      // error handled by slice
    } finally {
      setCancelReworkLoading(false);
    }
  };

  // ── Reject scrap record handlers ──────────────────────────────────────────────

  const handleOpenReject = (id: string) => {
    setRejectScrapId(id);
    setRejectionReason('');
    setRejectScrapOpen(true);
  };

  const handleConfirmReject = async () => {
    setRejectScrapLoading(true);
    try {
      await dispatch(rejectScrapRecord({ id: rejectScrapId, rejectionReason })).unwrap();
      setRejectScrapOpen(false);
      setRejectionReason('');
      setRejectScrapId('');
      dispatch(fetchScrapRecords({}));
      setSnackbarMessage('Record rejected');
      setSnackbarOpen(true);
    } catch {
      // error handled by slice
    } finally {
      setRejectScrapLoading(false);
    }
  };

  // ── Derived data for charts ──────────────────────────────────────────────────

  const reasonPieData = analytics?.reasonBreakdown?.map((item, i) => ({
    name:  SCRAP_REASON_LABELS[item.reason as ScrapReason] ?? item.reason,
    value: item.count,
    cost:  item.cost,
    color: PIE_COLORS[i % PIE_COLORS.length],
  })) ?? [];

  // ── Service unavailable banner ───────────────────────────────────────────────

  const ServiceUnavailableBanner = () => (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Scrap service not yet available. Contact your system administrator.
    </Alert>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Scrap Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setCreateReworkOpen(true)}
            disabled={serviceUnavailable}
          >
            Create Rework Order
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreateScrap}
            disabled={serviceUnavailable}
          >
            New Record
          </Button>
        </Box>
      </Box>

      {/* Error / unavailable banners */}
      {serviceUnavailable && <ServiceUnavailableBanner />}
      {error && !serviceUnavailable && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading bar */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary KPI Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Scrap Cost
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {analytics ? formatCurrency(analytics.totalScrapCost) : '—'}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Recovered Value
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {analytics ? formatCurrency(analytics.recoveredValue) : '—'}
                  </Typography>
                </Box>
                <Recycling sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Net Loss
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {analytics ? formatCurrency(analytics.netLoss) : '—'}
                  </Typography>
                </Box>
                <TrendingDown sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`Scrap Records (${scrapRecords.length})`} />
          <Tab label="Cost Dashboard" />
          <Tab label={`Rework Orders (${reworkOrders.length})`} />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Scrap Records ── */}
      {selectedTab === 0 && (
        <Box>
          {/* Filter bar */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Date From"
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Date To"
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Scrap Reason</InputLabel>
                  <Select
                    value={filterReason}
                    label="Scrap Reason"
                    onChange={(e) => setFilterReason(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {Object.values(ScrapReason).map((r) => (
                      <MenuItem key={r} value={r}>{SCRAP_REASON_LABELS[r]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={ScrapStatus.PENDING}>Pending</MenuItem>
                    <MenuItem value={ScrapStatus.APPROVED}>Approved</MenuItem>
                    <MenuItem value={ScrapStatus.REJECTED}>Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button variant="outlined" onClick={handleApplyFilters} disabled={isLoading} fullWidth>
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          {isLoading && scrapRecords.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : serviceUnavailable ? (
            <Alert severity="warning">
              Scrap service not yet available. Contact your system administrator.
            </Alert>
          ) : scrapRecords.length === 0 ? (
            <Alert severity="info">No scrap records found. Create one to get started.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Defect Type</TableCell>
                    <TableCell>Disposition</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Total Cost</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(scrapRecords as ScrapRecord[]).map((rec) => (
                    <TableRow key={rec.id}>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">{rec.id.slice(0, 8)}…</Typography>
                      </TableCell>
                      <TableCell>{rec.itemId}</TableCell>
                      <TableCell>{rec.batchNumber ?? '—'}</TableCell>
                      <TableCell align="right">{rec.quantity}</TableCell>
                      <TableCell>
                        <Chip
                          label={SCRAP_REASON_LABELS[rec.scrapReason] ?? rec.scrapReason}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: REASON_COLORS[rec.scrapReason], color: REASON_COLORS[rec.scrapReason] }}
                        />
                      </TableCell>
                      <TableCell>
                        {rec.defectType
                          ? rec.defectType.charAt(0).toUpperCase() + rec.defectType.slice(1)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={DISPOSITION_LABELS[rec.disposition] ?? rec.disposition}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rec.status.toUpperCase()}
                          color={getScrapStatusColor(rec.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {rec.scrapDate ? new Date(rec.scrapDate).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>{rec.reportedBy || '—'}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(rec.costs?.totalCost ?? 0)}
                      </TableCell>
                      <TableCell>
                        {rec.status === ScrapStatus.PENDING && (
                          <Box display="flex" gap={0.5}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<ThumbUp fontSize="small" />}
                              onClick={() => handleApprove(rec.id)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ThumbDown fontSize="small" />}
                              onClick={() => handleOpenReject(rec.id)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Reject
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Tab 1: Cost Dashboard ── */}
      {selectedTab === 1 && (
        <Box>
          {serviceUnavailable ? (
            <Alert severity="warning">
              Scrap service not yet available. Contact your system administrator.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {/* Cost Trend Line Chart */}
              <Grid item xs={12} lg={8}>
                <Paper sx={{ p: 2, height: 420 }}>
                  <Typography variant="h6" gutterBottom>
                    Scrap Cost Trend
                  </Typography>
                  {!analytics || analytics.costTrend.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No cost trend data available yet.
                    </Alert>
                  ) : (
                    <ResponsiveContainer width="100%" height="90%">
                      <LineChart data={analytics.costTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis tickFormatter={(v) => `KES ${v.toLocaleString()}`} />
                        <Tooltip formatter={(v: number) => formatCurrency(v)} />
                        <Line
                          type="monotone"
                          dataKey="cost"
                          stroke="#f44336"
                          strokeWidth={3}
                          name="Scrap Cost"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </Grid>

              {/* Reason Breakdown Pie Chart */}
              <Grid item xs={12} lg={4}>
                <Paper sx={{ p: 2, height: 420 }}>
                  <Typography variant="h6" gutterBottom>
                    Reason Breakdown
                  </Typography>
                  {reasonPieData.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No reason breakdown data available yet.
                    </Alert>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height="60%">
                        <PieChart>
                          <Pie
                            data={reasonPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={100}
                            paddingAngle={4}
                            dataKey="value"
                          >
                            {reasonPieData.map((entry, i) => (
                              <Cell key={`cell-${i}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v: number, _n, props) => [`${v} records`, props.payload?.name]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box mt={1}>
                        {reasonPieData.map((item) => (
                          <Box key={item.name} display="flex" justifyContent="space-between" mb={0.5}>
                            <Box display="flex" alignItems="center">
                              <Box
                                width={10}
                                height={10}
                                bgcolor={item.color}
                                borderRadius="50%"
                                mr={1}
                                flexShrink={0}
                              />
                              <Typography variant="caption">{item.name}</Typography>
                            </Box>
                            <Typography variant="caption" fontWeight="bold">
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* ── Tab 2: Rework Orders ── */}
      {selectedTab === 2 && (
        <Box>
          {isLoading && reworkOrders.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : serviceUnavailable ? (
            <Alert severity="warning">
              Scrap service not yet available. Contact your system administrator.
            </Alert>
          ) : reworkOrders.length === 0 ? (
            <Alert severity="info">No rework orders found. Create one to get started.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rework ID</TableCell>
                    <TableCell>Scrap Record Ref</TableCell>
                    <TableCell>Est. Cost</TableCell>
                    <TableCell>Est. Time (hrs)</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(reworkOrders as ReworkOrder[]).map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">{order.id.slice(0, 8)}…</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">{order.scrapRecordId.slice(0, 8)}…</Typography>
                      </TableCell>
                      <TableCell>{formatCurrency(order.estimatedCost)}</TableCell>
                      <TableCell>{order.estimatedTime}h</TableCell>
                      <TableCell>{order.assignedTo || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status.replace('_', ' ').toUpperCase()}
                          color={getReworkStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {order.status === ReworkStatus.PENDING && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<PlayArrow fontSize="small" />}
                            onClick={() => handleStartRework(order.id)}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Start Rework
                          </Button>
                        )}
                        {order.status === ReworkStatus.IN_PROGRESS && (
                          <Box display="flex" gap={0.5}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<Done fontSize="small" />}
                              onClick={() => handleOpenComplete(order.id)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Complete
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel fontSize="small" />}
                              onClick={() => handleOpenCancel(order.id)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        )}
                        {order.status === ReworkStatus.COMPLETED && (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                        {order.status === ReworkStatus.CANCELLED && (
                          <Chip label="Cancelled" size="small" sx={{ color: 'grey.600', borderColor: 'grey.400' }} variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* Create Scrap Record Dialog                                              */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={createScrapOpen}
        onClose={() => { setCreateScrapOpen(false); setScrapForm({ ...EMPTY_SCRAP_FORM }); setCreateScrapError(null); setSelectedItem(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>New Scrap Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {createScrapError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createScrapError}</Alert>
            )}
            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                {itemsFailed ? (
                  <TextField
                    label="Item ID"
                    value={scrapForm.itemId}
                    onChange={(e) => setScrapForm(p => ({ ...p, itemId: e.target.value }))}
                    fullWidth
                    required
                    placeholder="Enter Item ID"
                  />
                ) : (
                  <Autocomplete
                    options={items}
                    loading={itemsLoading}
                    value={selectedItem}
                    onChange={(_e, val) => {
                      setSelectedItem(val);
                      setScrapForm(p => ({ ...p, itemId: val?.id ?? '' }));
                    }}
                    getOptionLabel={(opt) => `${opt.itemCode} — ${opt.name}`}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {itemsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Batch Number"
                  value={scrapForm.batchNumber}
                  onChange={(e) => setScrapForm(p => ({ ...p, batchNumber: e.target.value }))}
                  fullWidth
                />
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={scrapForm.quantity}
                  onChange={(e) => setScrapForm(p => ({ ...p, quantity: Number(e.target.value) }))}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Scrap Reason</InputLabel>
                  <Select
                    value={scrapForm.scrapReason}
                    label="Scrap Reason"
                    onChange={(e) => setScrapForm(p => ({ ...p, scrapReason: e.target.value as ScrapReason }))}
                  >
                    {Object.values(ScrapReason).map((r) => (
                      <MenuItem key={r} value={r}>{SCRAP_REASON_LABELS[r]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Defect Type</InputLabel>
                  <Select
                    value={scrapForm.defectType}
                    label="Defect Type"
                    onChange={(e) => setScrapForm(p => ({ ...p, defectType: e.target.value }))}
                  >
                    <MenuItem value="visual">Visual</MenuItem>
                    <MenuItem value="dimensional">Dimensional</MenuItem>
                    <MenuItem value="functional">Functional</MenuItem>
                    <MenuItem value="material">Material</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Disposition</InputLabel>
                  <Select
                    value={scrapForm.disposition}
                    label="Disposition"
                    onChange={(e) => setScrapForm(p => ({ ...p, disposition: e.target.value as ScrapDisposition }))}
                  >
                    {Object.values(ScrapDisposition).map((d) => (
                      <MenuItem key={d} value={d}>{DISPOSITION_LABELS[d]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Costs */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="textSecondary">Cost Breakdown</Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Material Cost (KES)"
                  type="number"
                  value={scrapForm.materialCost}
                  onChange={(e) => setScrapForm(p => ({ ...p, materialCost: Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Labor Cost (KES)"
                  type="number"
                  value={scrapForm.laborCost}
                  onChange={(e) => setScrapForm(p => ({ ...p, laborCost: Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Overhead Cost (KES)"
                  type="number"
                  value={scrapForm.overheadCost}
                  onChange={(e) => setScrapForm(p => ({ ...p, overheadCost: Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Auto-calculated total */}
              <Grid item xs={12}>
                <Alert severity="info" icon={<AttachMoney />}>
                  Total Cost (auto-calculated): <strong>{formatCurrency(totalCost)}</strong>
                </Alert>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={scrapForm.notes}
                  onChange={(e) => setScrapForm(p => ({ ...p, notes: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateScrapOpen(false);
              setScrapForm({ ...EMPTY_SCRAP_FORM });
              setCreateScrapError(null);
              setSelectedItem(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateScrap}
            disabled={createScrapLoading || !scrapForm.itemId}
            startIcon={createScrapLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {createScrapLoading ? 'Creating…' : 'Create Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* Create Rework Order Dialog                                              */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={createReworkOpen}
        onClose={() => { setCreateReworkOpen(false); setReworkForm({ ...EMPTY_REWORK_FORM }); setCreateReworkError(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Rework Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {createReworkError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createReworkError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Scrap Record ID"
                  value={reworkForm.scrapRecordId}
                  onChange={(e) => setReworkForm(p => ({ ...p, scrapRecordId: e.target.value }))}
                  fullWidth
                  required
                  placeholder="Paste the scrap record ID"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Rework Instructions"
                  value={reworkForm.reworkInstructions}
                  onChange={(e) => setReworkForm(p => ({ ...p, reworkInstructions: e.target.value }))}
                  fullWidth
                  required
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated Cost (KES)"
                  type="number"
                  value={reworkForm.estimatedCost}
                  onChange={(e) => setReworkForm(p => ({ ...p, estimatedCost: Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated Time (hours)"
                  type="number"
                  value={reworkForm.estimatedTime}
                  onChange={(e) => setReworkForm(p => ({ ...p, estimatedTime: Number(e.target.value) }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Assigned To"
                  value={reworkForm.assignedTo}
                  onChange={(e) => setReworkForm(p => ({ ...p, assignedTo: e.target.value }))}
                  fullWidth
                  required
                  placeholder="Operator or team name"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateReworkOpen(false);
              setReworkForm({ ...EMPTY_REWORK_FORM });
              setCreateReworkError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCreateRework}
            disabled={
              createReworkLoading ||
              !reworkForm.scrapRecordId ||
              !reworkForm.reworkInstructions ||
              !reworkForm.assignedTo
            }
            startIcon={createReworkLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {createReworkLoading ? 'Creating…' : 'Create Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* Complete Rework Dialog                                                   */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={completeReworkOpen}
        onClose={() => { setCompleteReworkOpen(false); setCompletionNotes(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Complete Rework Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Completion Notes"
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Describe what was done (min 10 characters)"
              error={completionNotes.length > 0 && completionNotes.length < 10}
              helperText={completionNotes.length > 0 && completionNotes.length < 10 ? 'Minimum 10 characters required' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCompleteReworkOpen(false); setCompletionNotes(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmComplete}
            disabled={completeReworkLoading || completionNotes.trim().length < 10}
            startIcon={completeReworkLoading ? <CircularProgress size={16} /> : <Done />}
          >
            {completeReworkLoading ? 'Completing…' : 'Confirm Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* Cancel Rework Dialog                                                     */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={cancelReworkOpen}
        onClose={() => setCancelReworkOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Rework Order</DialogTitle>
        <DialogContent>
          <Typography>Cancel this rework order?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelReworkOpen(false)}>No, Keep It</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleConfirmCancel}
            disabled={cancelReworkLoading}
            startIcon={cancelReworkLoading ? <CircularProgress size={16} /> : <Cancel />}
          >
            {cancelReworkLoading ? 'Cancelling…' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* Reject Scrap Record Dialog                                               */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={rejectScrapOpen}
        onClose={() => { setRejectScrapOpen(false); setRejectionReason(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Scrap Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Describe why this record is being rejected (min 10 characters)"
              error={rejectionReason.length > 0 && rejectionReason.length < 10}
              helperText={rejectionReason.length > 0 && rejectionReason.length < 10 ? 'Minimum 10 characters required' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectScrapOpen(false); setRejectionReason(''); }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleConfirmReject}
            disabled={rejectScrapLoading || rejectionReason.trim().length < 10}
            startIcon={rejectScrapLoading ? <CircularProgress size={16} /> : <ThumbDown />}
          >
            {rejectScrapLoading ? 'Rejecting…' : 'Reject Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* Snackbar                                                                 */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Scrap;
